import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL } from '../../constants/apiConstants';
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployeeDocuments, uploadDocument, setUploadStatus } from '../../features/employeeDocuments/employeeDocumentsSlice';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: "#ffffff",
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid #e5e7eb",
}));

const EmployeeDocuments = () => {
  const dispatch = useDispatch();
  const { documents, loading, uploadStatus } = useSelector((state) => state.employeeDocuments);
  const [aadhaarDoc, setAadhaarDoc] = useState({ file: null, uploading: false, success: false, error: null });
  const [panDoc, setPanDoc] = useState({ file: null, uploading: false, success: false, error: null });
  const [passportDoc, setPassportDoc] = useState({ file: null, uploading: false, success: false, error: null });
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);


  // For additional documents
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [newDocName, setNewDocName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [generalError, setGeneralError] = useState(null);

  const handleUploadDocument = async (docType, file, updateStatus) => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const employeeId = userData?.employee?.id;

    if (!employeeId) {
      updateStatus({ uploading: false, success: false, error: 'Employee ID not found' });
      return;
    }

    const formData = new FormData();
    formData.append('document', file);
    formData.append('employeeId', employeeId);
    formData.append('documentType', docType);
    formData.append('remarks', '');

    try {
      await dispatch(uploadDocument({ employeeId, formData, docType })).unwrap();
    } catch (error) {
      updateStatus({
        uploading: false,
        success: false,
        error: error.message || 'Upload failed'
      });
    }
  };

  // Handle file selection and auto-upload
  const handleFileSelect = async (docType, e) => {
    const file = e.target.files[0];
    if (!file) return;

    let setDocState;
    switch (docType) {
      case 'Aadhaar Card':
        setDocState = setAadhaarDoc;
        break;
      case 'PAN Card':
        setDocState = setPanDoc;
        break;
      case 'Passport':
        setDocState = setPassportDoc;
        break;
      default:
        const docIndex = additionalDocs.findIndex(doc => doc.name === docType);
        if (docIndex !== -1) {
          const updatedDocs = [...additionalDocs];
          updatedDocs[docIndex] = {
            ...updatedDocs[docIndex],
            file,
            uploading: true,
            success: false,
            error: null
          };
          setAdditionalDocs(updatedDocs);
          await handleUploadDocument(docType, file, (status) => {
            const newDocs = [...additionalDocs];
            newDocs[docIndex] = {
              ...newDocs[docIndex],
              ...status
            };
            setAdditionalDocs(newDocs);
          });
          return;
        }
    }





    if (setDocState) {
      setDocState(prev => ({ ...prev, file, uploading: true, success: false, error: null }));
      await handleUploadDocument(docType, file, (status) => {
        setDocState(prev => ({ ...prev, ...status }));
      });
    }
  };

  // Move these OUTSIDE handleFileSelect - place after handleUploadDocument
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const employeeId = userData?.employee?.id;

    if (!employeeId) return;

    dispatch(fetchEmployeeDocuments(employeeId));
  }, [dispatch]);

  useEffect(() => {
    if (documents.length > 0) {
      setAdditionalDocs([]);
      documents.forEach(doc => {
        const docState = {
          file: null,
          uploading: false,
          success: true,
          error: null,
          documentUrl: doc.documentUrl.replace(/^https?:\/\/[^/]+/, '')
        };

        switch (doc.documentType) {
          case 'Aadhaar Card':
            setAadhaarDoc(docState);
            break;
          case 'PAN Card':
            setPanDoc(docState);
            break;
          case 'Passport':
            setPassportDoc(docState);
            break;
          default:
            setAdditionalDocs(prev => [
              ...prev,
              {
                name: doc.documentType,
                file: null,
                uploading: false,
                success: true,
                error: null,
                documentUrl: doc.documentUrl.replace(/^https?:\/\/[^/]+/, '')
              }
            ]);
            break;
        }
      });

      setExistingDocuments(documents);
    }
  }, [documents]);


  const openDocumentPreview = (documentUrl) => {
    const baseUrl = API_BASE_URL.replace('/api', '');
    setSelectedDocument(`${baseUrl}${documentUrl}`);
    setPreviewOpen(true);
  };




  const handleAddDocument = () => {
    if (!newDocName.trim()) {
      setGeneralError('Please enter a document name');
      return;
    }

    setAdditionalDocs([
      ...additionalDocs,
      { name: newDocName, file: null, uploading: false, success: false, error: null }
    ]);
    setNewDocName('');
    setShowAddForm(false);
    setGeneralError(null);
  };


  const removeDocument = (index) => {
    setAdditionalDocs(additionalDocs.filter((_, i) => i !== index));
  };


  const DocumentCard = ({ title, file, uploading, success, error, required, documentUrl, onFileSelect }) => (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: '10px', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px' }}>
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={500}>
                {title}
                {required && (
                  <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {file && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: { xs: '100px', sm: '150px' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mr: 1
                  }}
                >
                  {file.name}
                </Typography>
              )}

              <Button
                variant={success ? "outlined" : "contained"}
                component="label"
                size="small"
                disabled={uploading}
                startIcon={
                  uploading ? <CircularProgress size={16} /> :
                    success ? <CheckCircleIcon /> :
                      <UploadFileIcon />
                }
                color={success ? "success" : "primary"}
                sx={{ minWidth: '120px' }}
              >
                {uploading ? 'Uploading...' : success ? 'Uploaded' : 'Upload'}
                <input
                  type="file"
                  hidden
                  onChange={onFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </Button>

              {success && documentUrl && (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={() => openDocumentPreview(documentUrl)}
                  sx={{ ml: 1 }}
                >
                  Preview
                </Button>
              )}
            </Box>

            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
                {error}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <HeaderCard>
        <CardContent sx={{ py: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h5" fontWeight="700" color="#2563eb" gutterBottom>
                Upload Documents
              </Typography>
              <Typography variant="body2" color="#6b7280">
                As part of completing your onboarding, please submit the necessary documents for verification and recordkeeping in the company portal
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", bgcolor: "#f8fafc", borderRadius: 2, p: 2, border: "1px solid #e5e7eb" }}>
              <Typography variant="h4" fontWeight="700" color="#1f2937">
                {Object.values({ aadhaarDoc, panDoc, passportDoc }).filter(doc => doc.success).length + additionalDocs.filter(doc => doc.success).length}
                /
                {3 + additionalDocs.length}
              </Typography>
              <Typography variant="caption" color="#6b7280">
                Documents Uploaded
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>


      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>

        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
            Required Documents
          </Typography>
          <DocumentCard
            title="Aadhaar Card"
            file={aadhaarDoc.file}
            uploading={aadhaarDoc.uploading}
            success={aadhaarDoc.success}
            error={aadhaarDoc.error}
            documentUrl={aadhaarDoc.documentUrl}
            required={true}
            onFileSelect={(e) => handleFileSelect('Aadhaar Card', e)}
          />

          <DocumentCard
            title="PAN Card"
            file={panDoc.file}
            uploading={panDoc.uploading}
            success={panDoc.success}
            error={panDoc.error}
            documentUrl={panDoc.documentUrl}
            required={true}
            onFileSelect={(e) => handleFileSelect('PAN Card', e)}
          />

          <DocumentCard
            title="Passport (optional)"
            file={passportDoc.file}
            uploading={passportDoc.uploading}
            success={passportDoc.success}
            error={passportDoc.error}
            documentUrl={passportDoc.documentUrl}
            onFileSelect={(e) => handleFileSelect('Passport', e)}
          />

          {additionalDocs.map((doc, index) => (
            <Card variant="outlined" sx={{ mb: 2, borderRadius: '10px' }} key={`doc-${index}`}>
              <CardContent>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1">{doc.name}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      {doc.file && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: { xs: '80px', sm: '120px' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mr: 1
                          }}
                        >
                          {doc.file.name}
                        </Typography>
                      )}

                      <Button
                        variant={doc.success ? "outlined" : "contained"}
                        component="label"
                        size="small"
                        disabled={doc.uploading}
                        startIcon={
                          doc.uploading ? <CircularProgress size={16} /> :
                            doc.success ? <CheckCircleIcon /> :
                              <UploadFileIcon />
                        }
                        color={doc.success ? "success" : "primary"}
                        sx={{ minWidth: '120px' }}
                      >
                        {doc.uploading ? 'Uploading...' : doc.success ? 'Uploaded' : 'Upload'}
                        <input
                          type="file"
                          hidden
                          onChange={(e) => handleFileSelect(doc.name, e)}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                      </Button>

                      {doc.success && doc.documentUrl && (
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={() => openDocumentPreview(doc.documentUrl)}
                          sx={{ ml: 1 }}
                        >
                          Preview
                        </Button>
                      )}
                    </Box>

                    {doc.error && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
                        {doc.error}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>

        {showAddForm ? (
          <Card variant="outlined" sx={{ mb: 2, p: 2, borderRadius: '10px' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={7}>
                <TextField
                  fullWidth
                  size="small"
                  label="Document Name"
                  value={newDocName}
                  onChange={(e) => setNewDocName(e.target.value)}
                  placeholder="Enter document name"
                />
              </Grid>
              <Grid item xs={5}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleAddDocument}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewDocName('');
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Card>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(true)}
            sx={{ mt: 2 }}
          >
            Add New Required Document
          </Button>

        )}
      </Paper>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          {selectedDocument && (
            selectedDocument.endsWith('.pdf') ? (
              <iframe
                src={selectedDocument}
                width="100%"
                height="500px"
                title="Document Preview"
              />
            ) : (
              <img
                src={selectedDocument}
                alt="Document Preview"
                style={{ maxWidth: '100%', maxHeight: '500px', margin: 'auto', display: 'block' }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default EmployeeDocuments;